import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * RxAssistant message → sanitized HTML for `dangerouslySetInnerHTML`.
 * Ported verbatim from v1's `advisorChatMarkdown.ts` so renderings match.
 *
 * Two paths based on `contentType`:
 *   text/markdown → marked → DOMPurify
 *   text/plain    → escape + <br/> → wrap in <p>
 *
 * All anchor tags get `target="_blank"` + `rel="noopener noreferrer"` via
 * a DOMPurify hook so links open safely in a new tab without referrer leak.
 */
export type AdvisorChatMessageForRender = {
  content: string;
  contentType: 'text/markdown' | 'text/plain';
  role: 'user' | 'assistant';
};

let anchorHookInstalled = false;

/** Install the anchor-rewrite hook exactly once (DOMPurify is a global
 *  singleton; adding the hook on every render would stack handlers). */
const ensureAnchorSecurityHook = (): void => {
  if (anchorHookInstalled) return;
  anchorHookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A' || !(node as Element).hasAttribute('href')) return;
    const href = (node as Element).getAttribute('href') ?? '';
    if (/^https?:\/\//i.test(href) || href.startsWith('/')) {
      (node as Element).setAttribute('target', '_blank');
      (node as Element).setAttribute('rel', 'noopener noreferrer');
    }
  });
};

// Override marked's default link renderer so links carry target/rel even
// before the DOMPurify hook runs (defense in depth).
marked.use({
  gfm: true,
  breaks: true,
  renderer: {
    link(token) {
      const text = this.parser.parseInline(token.tokens);
      const titleAttr = token.title != null ? ` title="${token.title}"` : '';
      return `<a href="${token.href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  },
});

const escapePlainTextForHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br/>');

const shouldRenderAsMarkdown = (m: AdvisorChatMessageForRender): boolean =>
  m.contentType === 'text/markdown';

export const formatAdvisorChatMessageHtml = (
  message: AdvisorChatMessageForRender,
): string => {
  ensureAnchorSecurityHook();
  const raw = message.content ?? '';
  if (!raw) return '';

  const asMarkdown = shouldRenderAsMarkdown(message);
  const htmlBody = asMarkdown
    ? (marked.parse(raw, { async: false }) as string)
    : `<p>${escapePlainTextForHtml(raw)}</p>`;

  const wrapperClass = asMarkdown ? 'rx-md' : 'rx-md rx-md--plaintext';

  return DOMPurify.sanitize(`<div class="${wrapperClass}">${htmlBody}</div>`, {
    ADD_ATTR: ['target', 'rel'],
  });
};

const markdownToPlainTextForCopy = (markdown: string): string => {
  const html = marked.parse(markdown, { async: false }) as string;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent ?? '';
};

export const messageContentToPlainTextForCopy = (
  message: AdvisorChatMessageForRender,
): string => {
  if (message.contentType === 'text/plain') return message.content;
  return markdownToPlainTextForCopy(message.content);
};
